import axios from 'axios';
import { getIncidentsApiBase } from '@/lib/incidentsApi';
import { formatApiError } from '@/lib/vibeChatApi';

/** Deploy / ATC / Import go through incidents (gateway → incidents → master). */
function getAbapApiBase() {
  const apiBase = (getIncidentsApiBase() || '').trim().replace(/\/$/, '');
  if (!apiBase) {
    throw new Error(
      'Incidents API URL is not configured (set REACT_APP_INCIDENTS_API_URL or REACT_APP_BACKEND_URL)',
    );
  }
  return apiBase;
}

/**
 * POST /abap/import-code — fetch an existing SAP program and create a new Import ticket.
 */
export async function importAbapCode({
  programName,
  requesterName = '',
  requesterEmail = '',
  sapSystem = 'S/4HANA Cloud',
  targetSystemId = '',
  targetSystemName = '',
  priority = 'medium',
}) {
  const response = await axios.post(`${getAbapApiBase()}/abap/import-code`, {
    program_name: programName,
    requester_name: requesterName,
    requester_email: requesterEmail,
    sap_system: sapSystem,
    target_system_id: targetSystemId,
    target_system_name: targetSystemName,
    priority,
  });
  return response.data;
}

/**
 * POST /abap/syntax-check — standalone SAP syntax check (no write/activate).
 */
export async function runAbapSyntaxCheck({ code, objectName = 'UNKNOWN' }) {
  const response = await axios.post(`${getAbapApiBase()}/abap/syntax-check`, {
    code,
    object_name: objectName,
  });
  return response.data;
}

/**
 * POST /abap/get-source — fetch the current live SAP source for an object.
 * Used to build a git-style diff against locally edited code before deploy.
 */
export async function getLiveAbapSource({ objectName, objectType = 'PROG' }) {
  const response = await axios.post(`${getAbapApiBase()}/abap/get-source`, {
    object_name: objectName,
    object_type: objectType,
  });
  return response.data;
}

/**
 * POST /abap/fix-code — AI-correct code from a syntax check or ATC failure.
 * Does NOT deploy — returns corrected code for local review (diff) before deploy.
 */
export async function fixAbapCode({
  code,
  source, // 'syntax' | 'atc'
  syntaxReport = null,
  atcResult = null,
  originalRequest = '',
  ticketNumber = '',
}) {
  const response = await axios.post(`${getAbapApiBase()}/abap/fix-code`, {
    code,
    source,
    syntax_report: syntaxReport,
    atc_result: atcResult,
    original_request: originalRequest,
    ticket_number: ticketNumber,
  });
  return response.data;
}

/** Parse "Import: Z_FOO (PROG)" → { objectName: 'Z_FOO', objectType: 'PROG' }. */
export function parseImportTicketTitle(title) {
  const match = /^Import:\s*(.+?)\s*\(([A-Z]+)\)\s*$/.exec((title || '').trim());
  if (!match) {
    return { objectName: (title || '').replace(/^Import:\s*/, '').trim(), objectType: 'PROG' };
  }
  return { objectName: match[1], objectType: match[2] };
}

export { formatApiError };
